import React, { useState, useEffect, useRef } from 'react';
import { Button, Form, FormGroup, FormLabel, FormControl, FormCheck, Row, Col, Container, ListGroup, ListGroupItem, InputGroup } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import '../AdminDashboard.css';
import axios from 'axios';

const UserAccountsPage = ({ ViewedPage, app_url }) => {
  const { email } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stores, setStores] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Dev page permissions state
  const [allDevPageMapping, setAllDevPageMapping] = useState(null);
  const [devPageMappingLoading, setDevPageMappingLoading] = useState(false);
  const [devPagePermissionsByStore, setDevPagePermissionsByStore] = useState({});
  const [selectedStoreForPermissions, setSelectedStoreForPermissions] = useState('');
  // Only stores admin's MANUAL checkbox changes this session.
  // Display logic reads from DB + this state via getPermissionsForStore().
  const manuallyModifiedStores = useRef(new Set());
  
  // Helper function to normalize store key (remove .myshopify.com suffix)
  const normalizeStoreKey = (store) => {
    if (!store) return '';
    return store.replace('.myshopify.com', '');
  };
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('');

  useEffect(() => {
    ViewedPage(101);
    const loadData = async () => {
      try {
        await Promise.all([
          fetchUserData(),
          fetchStoresData(),
          fetchAllDevPageMapping()
        ]);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [email, ViewedPage]);

  // Auto-select the first store when page loads with assigned stores
  useEffect(() => {
    if (user && selectedStores.length > 0 && !selectedStoreForPermissions) {
      setSelectedStoreForPermissions(normalizeStoreKey(selectedStores[0]));
    }
  }, [user, selectedStores]);

  const fetchUserData = async () => {
    try {
      const response = await axios.get(app_url + '/api/admin/users', { withCredentials: true });
      const data = response.data;
      if (data.success) {
        const foundUser = data.data.find(u => u.email === email);
        if (foundUser) {
          let cleaned_stores_json = foundUser?.stores ? foundUser.stores.map(store => store.replace(".myshopify.com","")) : [];
          setUser(foundUser);
          setSelectedStores(cleaned_stores_json || []);
        } else {
          setMessage({ type: 'error', text: 'User not found' });
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setMessage({ type: 'error', text: 'Failed to fetch user data' });
      throw error; // Re-throw to be caught by Promise.all
    }
  };

  const fetchStoresData = async () => {
    try {
      const response = await axios.get(app_url + '/api/admin/stores', { withCredentials: true });
      const data = response.data;
      if (data.success) {
        setStores(data.data);
      }
    } catch (error) {
      console.error('Error fetching stores data:', error);
      setMessage({ type: 'error', text: 'Failed to fetch stores data' });
      throw error; // Re-throw to be caught by Promise.all
    }
  };

  const fetchAllDevPageMapping = async () => {
    try {
      setDevPageMappingLoading(true);
      const response = await axios.get(app_url + '/api/admin/all-dev-page-mapping', { withCredentials: true });
      const data = response.data;
      
      if (data.success && data.data && data.data.all_dev_page_mapping) {
        setAllDevPageMapping(data.data.all_dev_page_mapping);
      } else {
        setAllDevPageMapping(null);
      }
    } catch (error) {
      console.error('Error fetching dev page mapping:', error);
      setAllDevPageMapping(null);
    } finally {
      setDevPageMappingLoading(false);
    }
  };

  // Pure function: computes permissions from DB data only (no state).
  // Priority: dev_page_permissions_by_store[store] → dev_page_permissions → []
  const computeDefaultPermissions = (storeKey) => {
    const savedPermsRaw = user?.dev_page_permissions_by_store || {};
    const perStoreDB =
      savedPermsRaw[storeKey] ??
      savedPermsRaw[storeKey + '.myshopify.com'] ??
      null;
    if (perStoreDB) return perStoreDB;
    return user?.dev_page_permissions || [];
  };

  // What to display/save for a store.
  // If admin manually edited it this session → use those edits.
  // Otherwise → compute fresh from DB (no stale state, no flash).
  const getPermissionsForStore = (storeKey) => {
    if (manuallyModifiedStores.current.has(storeKey) && devPagePermissionsByStore[storeKey] !== undefined) {
      return devPagePermissionsByStore[storeKey];
    }
    return computeDefaultPermissions(storeKey);
  };

  const handleStoreToggle = (store, checked) => {
    const storeKey = normalizeStoreKey(store);
    if (checked) {
      setSelectedStores(prev => [...prev, store]);
      // Auto-select in dropdown if nothing selected yet
      setSelectedStoreForPermissions(prev => prev || storeKey);
    } else {
      setSelectedStores(prev => prev.filter(s => s !== store));
      // Clean up any manual edits for this store
      manuallyModifiedStores.current.delete(storeKey);
      setDevPagePermissionsByStore(prev => {
        const next = { ...prev };
        delete next[storeKey];
        return next;
      });
      setSelectedStoreForPermissions(prev => (prev === storeKey ? '' : prev));
    }
  };

  const handleStorePagePermissionChange = (storeShop, pageName, checked) => {
    const storeKey = normalizeStoreKey(storeShop);

    setDevPagePermissionsByStore(prev => {
      // Seed from DB defaults the first time this store is manually edited
      const base = manuallyModifiedStores.current.has(storeKey) && prev[storeKey] !== undefined
        ? prev[storeKey]
        : computeDefaultPermissions(storeKey);

      const updated = checked
        ? (base.includes(pageName) ? base : [...base, pageName])
        : base.filter(p => p !== pageName);

      manuallyModifiedStores.current.add(storeKey);
      return { ...prev, [storeKey]: updated };
    });
  };

  // Filter and sort functions
  const getFilteredStores = () => {
    let filtered = stores;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(store => 
        store.shop.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (store.shop_name && store.shop_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Apply sorting
    if (sortBy) {
      filtered = [...filtered].sort((a, b) => {
        switch (sortBy) {
          case 'name-asc':
            return (a.shop_name || a.shop).localeCompare(b.shop_name || b.shop);
          case 'name-desc':
            return (b.shop_name || b.shop).localeCompare(a.shop_name || a.shop);

          case 'features-asc':
            const aFeatures = a.allowed_pages ? Object.keys(a.allowed_pages).filter(page => a.allowed_pages[page]).length : 0;
            const bFeatures = b.allowed_pages ? Object.keys(b.allowed_pages).filter(page => b.allowed_pages[page]).length : 0;
            return aFeatures - bFeatures;
          case 'features-desc':
            const aFeaturesDesc = a.allowed_pages ? Object.keys(a.allowed_pages).filter(page => a.allowed_pages[page]).length : 0;
            const bFeaturesDesc = b.allowed_pages ? Object.keys(b.allowed_pages).filter(page => b.allowed_pages[page]).length : 0;
            return bFeaturesDesc - aFeaturesDesc;
          default:
            return 0;
        }
      });
    }

    return filtered;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!user) return;

    try {
      setSaving(true);
      // Build final permissions for every selected store using getPermissionsForStore
      // so saves reflect DB defaults + any manual edits made this session.
      const finalDevPagePermissions = {};
      selectedStores.forEach(store => {
        const storeKey = normalizeStoreKey(store);
        finalDevPagePermissions[storeKey] = getPermissionsForStore(storeKey);
      });

      const response = await axios.post(app_url + '/api/admin/update-user-stores', {
        email: user.email,
        stores: selectedStores,
        dev_page_permissions_by_store: finalDevPagePermissions
      }, { withCredentials: true });

      const data = response.data;
      if (data.success) {
        setMessage({ type: 'success', text: 'User accounts and dev page permissions updated successfully!' });
        setTimeout(() => {
          navigate('/admin');
        }, 1500);
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to update user accounts' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error updating user accounts' });
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="main-container">
        <div className="admin-loading-inline">
          <div className="admin-loading-content">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading user data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="main-container">
        <div className="admin-header">
          <h1 className="admin-title">User Not Found</h1>
          <p className="admin-subtitle">The requested user could not be found</p>
          <Button variant="primary" onClick={handleBack} className="admin-btn">
            Back to Admin Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="main-container">
      <div className="admin-header">
        <div className="admin-header-content">
          <div className="admin-header-left">
            <h1 className="admin-title">Manage User Accounts</h1>
            <p className="admin-subtitle">
              Assign accounts to: <strong>{user.email}</strong>
            </p>
          </div>
          <div className="admin-header-right">
            <Button variant="outline-secondary" onClick={handleBack} className="admin-btn">
              ← Back to Dashboard
            </Button>
          </div>
        </div>
      </div>

      {message.text && (
        <div className={`admin-message admin-message-${message.type}`}>
          {message.text}
          <Button 
            variant="link" 
            className="admin-message-close"
            onClick={() => setMessage({ type: '', text: '' })}
          >
            ×
          </Button>
        </div>
      )}

      <Container fluid className="admin-content">
        <Row>
          <Col className="mx-auto">
            <div className="admin-section">
              <div className="admin-section-header">
                <h3 className="admin-section-title">User Information</h3>
              </div>
              
              <div className="admin-info-grid">
                <div className="admin-info-item">
                  <label className="admin-info-label">Email:</label>
                  <span className="admin-info-value">{user.email}</span>
                </div>
                <div className="admin-info-item">
                  <label className="admin-info-label">Role:</label>
                  <span className="admin-info-value">{user.role || 'user'}</span>
                </div>
                <div className="admin-info-item">
                  <label className="admin-info-label">OTP:</label>
                  <span className="admin-info-value">{user.otp || 'N/A'}</span>
                </div>
                {/* No Password row: passwords are stored hashed and never sent to this page. */}
                <div className="admin-info-item">
                  <label className="admin-info-label">Assigned Accounts:</label>
                  <span className="admin-info-value">
                    {selectedStores.length} / {stores.length}
                  </span>
                </div>
              </div>
            </div>

            {/* Dev Page Permissions Section - always visible */}
            <div className="admin-section">
              <div className="admin-section-header">
                <h3 className="admin-section-title">Dev Page Permissions by Store</h3>
                <p className="admin-section-description">
                  Select a store and assign dev page permissions for that store
                </p>
              </div>
                
                {devPageMappingLoading ? (
                  <div className="admin-loading-inline">
                    <div className="admin-loading-content">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2">Loading dev page mapping...</p>
                    </div>
                  </div>
                ) : allDevPageMapping ? (
                  <div className="admin-dev-permissions-section">
                    {/* Store Selection Dropdown */}
                    <Row className="mb-4">
                      <Col md={6}>
                        <FormGroup>
                          <FormLabel>Select Store</FormLabel>
                          <FormControl
                            as="select"
                            value={selectedStoreForPermissions}
                            onChange={(e) => setSelectedStoreForPermissions(e.target.value)}
                            className="admin-sort-select"
                          >
                            <option value="">-- Select a store --</option>
                            {/* Show stores that are in devPagePermissionsByStore (per-store data exists)
                                plus any selectedStore not yet in it (will use global dev_page_permissions) */}
                            {selectedStores.map(storeShop => {
                              const storeKey = normalizeStoreKey(storeShop);
                              const store = stores.find(s => normalizeStoreKey(s.shop) === storeShop);
                              const hasPerStorePerms = !!(user?.dev_page_permissions_by_store?.[storeKey] ||
                                user?.dev_page_permissions_by_store?.[storeShop + '.myshopify.com']);
                              return (
                                <option key={storeShop} value={storeShop}>
                                  {store?.shop_name || storeShop} ({storeShop}){hasPerStorePerms ? '' : ' — default'}
                                </option>
                              );
                            })}
                          </FormControl>
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* Permissions for Selected Store */}
                    {selectedStoreForPermissions && (
                      <div className="admin-store-permissions-block">
                        <div className="admin-store-permissions-header mb-3">
                          <h5 className="admin-subsection-title">
                            {(() => {
                              const store = stores.find(s => s.shop === selectedStoreForPermissions);
                              return store?.shop_name || selectedStoreForPermissions;
                            })()}
                          </h5>
                          <p className="text-muted mb-3">
                            <small>{selectedStoreForPermissions}</small>
                          </p>
                        </div>
                        <div className="admin-accounts-grid">
                          {Object.keys(allDevPageMapping).map((pageName) => {
                            const storePermissions = getPermissionsForStore(normalizeStoreKey(selectedStoreForPermissions));
                            const isChecked = storePermissions.includes(pageName);
                            
                            return (
                              <div key={pageName} className="admin-account-item">
                                <FormCheck
                                  type="checkbox"
                                  id={`dev-permission-${selectedStoreForPermissions}-${pageName}`}
                                  checked={isChecked}
                                  onChange={(e) => handleStorePagePermissionChange(selectedStoreForPermissions, pageName, e.target.checked)}
                                  label={pageName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                                  className="admin-checkbox-large"
                                />
                                <div className="admin-account-details">
                                  <div className="admin-account-shop">{pageName}</div>
                                  <div className="admin-account-features">
                                    Page ID: {allDevPageMapping[pageName]}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {!selectedStoreForPermissions && (
                      <div className="text-muted text-center py-4">
                        {selectedStores.length === 0 ? (
                          <p>
                            No stores assigned yet. Assign stores from the <strong>Account Assignment</strong> section below —
                            permissions will be pre-filled from this user's <strong>dev_page_permissions</strong>
                            {user?.dev_page_permissions?.length > 0
                              ? `: ${user.dev_page_permissions.join(', ')}`
                              : ' (none set, all pages will be enabled)'}.
                          </p>
                        ) : (
                          <p>Select a store from the dropdown above to view or edit its dev page permissions.</p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-muted">
                    <p>Dev page mapping not found in profit_x_settings collection</p>
                  </div>
                )}
            </div>

            <div className="admin-section">
              <div className="admin-section-header">
                <h3 className="admin-section-title">Account Assignment</h3>
                <p className="admin-section-description">
                  Select which accounts this user should have access to
                </p>
              </div>
              
              <Form onSubmit={handleSave}>
                {/* Search and Filter Controls */}
                <Row className="admin-controls-row">
                  <Col md={6}>
                    <InputGroup className="admin-search-group">
                      <InputGroup.Text className="admin-search-icon">🔍</InputGroup.Text>
                      <FormControl
                        type="text"
                        placeholder="Search accounts..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="admin-search-input"
                      />
                    </InputGroup>
                  </Col>
                  <Col md={6}>
                    <FormControl
                      as="select"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="admin-sort-select"
                    >
                      <option value="">Sort by</option>
                      <option value="name-asc">Client Name (A-Z)</option>
                      <option value="name-desc">Client Name (Z-A)</option>
                      <option value="features-asc">Features (Asc)</option>
                      <option value="features-desc">Features (Desc)</option>
                    </FormControl>
                  </Col>
                </Row>

                <div className="admin-accounts-grid">
                  {getFilteredStores().length === 0 ? (
                    <p className="admin-no-data">
                      {searchTerm ? 'No accounts match your search criteria' : 'No accounts found'}
                    </p>
                  ) : (
                    getFilteredStores().map(store => (
                      <div key={store.shop} className="admin-account-item">
                        <FormCheck
                          type="checkbox"
                          id={`store-${store.shop}`}
                          checked={selectedStores.includes(store.shop)}
                          onChange={(e) => handleStoreToggle(store.shop, e.target.checked)}
                          label={store.shop_name || store.shop}
                          className="admin-checkbox-large"
                        />
                        <div className="admin-account-details">
                          <div className="admin-account-shop">{store.shop}</div>
                          <div className="admin-account-features">
                            {store.allowed_pages ? Object.keys(store.allowed_pages).filter(page => store.allowed_pages[page]).length : 0} features enabled
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>



                <div className="admin-form-actions">
                  <Button 
                    type="button" 
                    variant="outline-secondary" 
                    onClick={handleBack}
                    className="admin-btn"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={saving} 
                    className="admin-btn admin-btn-primary"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </Form>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default UserAccountsPage;
